import { useCallback, useEffect, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { getHistory } from '../api/history'
import type { HistoryResolution, HistoryResponse } from '../api/historyTypes'
import { HistoryPoints, HistorySeries, toPoints, toSeries } from '../lib/history'

/**
 * v1.3.2 — finer detail while zoomed.
 *
 * When the customer zooms into a span that the API would serve at a finer
 * resolution than the one on screen (daily -> hourly -> 5-minute -> 30 s),
 * this fetches just that span, after a short pause so a quick series of
 * zooms sends one request. The page shows the detail in place of the coarse
 * data until the zoom is reset or a new range is chosen.
 *
 * Fetched spans are kept in memory (last DETAIL_CACHE): zooming back into an
 * area already covered at the right resolution is instant, with no request.
 */

export interface HistoryDetail {
  resp: HistoryResponse
  points: HistoryPoints
  series: HistorySeries
  /** The window requested; the chart holds this view when the detail arrives. */
  from: number
  to: number
}

const DEBOUNCE_MS = 350
const DETAIL_CACHE = 10
const RANK: Record<HistoryResolution, number> = { '30s': 0, '5min': 1, hour: 2, day: 3 }

/** Same rule as the API's choose_resolution() (app/history.py), including its 1-minute tolerance. */
export function resolutionFor(from: number, to: number, rawFrom: number): HistoryResolution {
  const span = to - from - 60
  if (span <= 36 * 3600 && from >= rawFrom) return '30s'
  if (span <= 14 * 86400) return '5min'
  if (span <= 92 * 86400) return 'hour'
  return 'day'
}

export const finer = (a: HistoryResolution, b: HistoryResolution) => RANK[a] < RANK[b]

/**
 * @param view     the visible window
 * @param zoomed   whether the customer has zoomed in (detail is only fetched for a zoom,
 *                 or to go finer still while detail is already showing)
 * @param baseRes  resolution of the range's own (coarse) data; the detail's, when showing, wins
 * @param rawFrom  earliest instant 30 s data exists (from the base response)
 * @param resetKey changes when the range or zoom is reset; drops the detail and pending work
 * @param cacheKey changes when the range changes; also empties the cache (a zoom reset keeps it,
 *                 so zooming back into an area already seen is instant)
 */
export function useHistoryDetail(
  systemId: number,
  view: { from: number; to: number } | null,
  zoomed: boolean,
  baseRes: HistoryResolution | null,
  rawFrom: number,
  resetKey: string,
  cacheKey: string,
) {
  const { instance } = useMsal()
  const [detail, setDetail] = useState<HistoryDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const reqId = useRef(0)
  const cache = useRef<HistoryDetail[]>([])
  const active = useRef(false)
  active.current = detail != null
  const shown: HistoryResolution | null = detail?.resp.resolution ?? baseRes

  const clear = useCallback(() => {
    reqId.current++
    setDetail(null)
    setLoading(false)
  }, [])

  // A zoom reset (or a new range) starts again from the coarse data.
  useEffect(() => { clear() }, [resetKey, systemId, clear])
  useEffect(() => { cache.current = [] }, [cacheKey, systemId])

  useEffect(() => {
    if (!view || !shown || (!zoomed && !active.current)) return
    const from = Math.floor(view.from), to = Math.ceil(view.to)
    if (to - from < 60) return
    const target = resolutionFor(from, to, rawFrom)
    if (!finer(target, shown)) return

    // Already fetched at this resolution and covering the whole view: use it, no request.
    const hit = cache.current.find((c) => c.resp.resolution === target && c.from <= from && c.to >= to)
    if (hit) {
      setDetail({ ...hit, from, to })
      return
    }

    const id = ++reqId.current
    const timer = window.setTimeout(async () => {
      if (id !== reqId.current) return
      setLoading(true)
      try {
        const resp = await getHistory(instance, { systemId, from, to })
        if (id !== reqId.current) return
        const points = toPoints(resp)
        // An empty or no-finer answer (e.g. a gap in the data) keeps the coarse view.
        if (points.t.length === 0 || !finer(resp.resolution, shown)) { setLoading(false); return }
        const d: HistoryDetail = { resp, points, series: toSeries(points), from, to }
        cache.current = [d, ...cache.current].slice(0, DETAIL_CACHE)
        setDetail(d)
      } catch {
        // Detail is a nice-to-have: on failure the coarse data stays on screen.
      } finally {
        if (id === reqId.current) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [instance, systemId, view?.from, view?.to, zoomed, shown, rawFrom]) // eslint-disable-line react-hooks/exhaustive-deps

  return { detail, loading, clear }
}
